import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://moyobed.pro';

function SummaryTab({ companyId }) {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' или 'list'
  const [expandedDay, setExpandedDay] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');

  const fetchSummary = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = { company_id: companyId };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      const response = await axios.get(`${API_URL}/api/admin/orders-summary`, { params });
      setSummary(response.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [companyId]);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/branches`, { params: { company_id: companyId } });
      setBranches(res.data);
    } catch (err) { console.error(err); }
  }, [companyId]);

  useEffect(() => { 
    fetchSummary(); 
    fetchBranches(); 
  }, [fetchSummary, fetchBranches]);

  // Подсчёт итогов
  const totalDishes = summary.reduce((sum, day) => 
    sum + Object.values(day.dishes).reduce((s, c) => s + c, 0), 0
  );
  const totalDays = summary.length;

  return (
    <div className="tab-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <h2 className="tab-title" style={{ margin: 0 }}>📊 Сводка заказов</h2>
        <div style={{ display: 'flex', gap: 6 }}>
          <button 
            className={`btn btn-sm ${viewMode === 'cards' ? '' : 'btn-outline'}`}
            onClick={() => setViewMode('cards')}
          >
            🗂️ Плитки
          </button>
          <button 
            className={`btn btn-sm ${viewMode === 'list' ? '' : 'btn-outline'}`}
            onClick={() => setViewMode('list')}
          >
            📋 Список
          </button>
        </div>
      </div>

      {/* Статистика */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="card" style={{ flex: 1, minWidth: 120, textAlign: 'center', padding: '14px 16px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--primary)' }}>{totalDays}</div>
          <div style={{ fontSize: 13, color: 'var(--text-light)' }}>дней</div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 120, textAlign: 'center', padding: '14px 16px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{totalDishes}</div>
          <div style={{ fontSize: 13, color: 'var(--text-light)' }}>блюд заказано</div>
        </div>
        <div className="card" style={{ flex: 1, minWidth: 120, textAlign: 'center', padding: '14px 16px' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b' }}>
            {summary.length > 0 ? Math.round(totalDishes / totalDays) : 0}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-light)' }}>в среднем / день</div>
        </div>
      </div>

      {/* Фильтры */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="date" className="input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 'auto', minWidth: 140 }} />
          <span style={{ color: 'var(--text-light)' }}>—</span>
          <input type="date" className="input" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 'auto', minWidth: 140 }} />
          <button className="btn btn-sm" onClick={() => fetchSummary(dateFrom || undefined, dateTo || undefined)}>🔍</button>
          <button className="btn btn-sm btn-outline" onClick={() => { setDateFrom(''); setDateTo(''); fetchSummary(); }}>🔄 Сброс</button>
        </div>
      </div>

      {loading ? (
        <div className="loading-screen"><div className="loading-spinner"></div></div>
      ) : summary.length === 0 ? (
        <div className="empty-state card"><p>📭 Нет заказов</p></div>
      ) : viewMode === 'cards' ? (
        /* ========== РЕЖИМ ПЛИТОК ========== */
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
          {summary.map(day => {
            const dayTotal = Object.values(day.dishes).reduce((s, c) => s + c, 0);
            const isExpanded = expandedDay === day.date;
            const topDishes = Object.entries(day.dishes)
              .sort((a, b) => b[1] - a[1])
              .slice(0, isExpanded ? 99 : 3);

            return (
              <div key={day.date} className="card" style={{ cursor: 'pointer' }} onClick={() => setExpandedDay(isExpanded ? null : day.date)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ fontSize: 15 }}>
                    {new Date(day.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                  </strong>
                  <span style={{ 
                    background: 'var(--primary)', 
                    color: 'white', 
                    borderRadius: 20, 
                    padding: '2px 10px', 
                    fontSize: 13, 
                    fontWeight: 600 
                  }}>
                    {dayTotal}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-light)', marginBottom: 8 }}>{day.day_of_week}</div>
                
                {topDishes.map(([name, count]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid var(--border)' }}>
                    <span style={{ flex: 1, marginRight: 8 }}>{name.slice(0, 40)}{name.length > 40 ? '...' : ''}</span>
                    <span style={{ fontWeight: 600, color: '#10b981' }}>{count} шт.</span>
                  </div>
                ))}
                
                {Object.keys(day.dishes).length > 3 && (
                  <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: 'var(--primary)' }}>
                    {isExpanded ? '▲ Свернуть' : `▼ Ещё ${Object.keys(day.dishes).length - 3} блюд`}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* ========== РЕЖИМ СПИСКА ========== */
        <div>
          {summary.map(day => (
            <div key={day.date} className="card" style={{ marginBottom: 12 }}>
              <h3 style={{ marginBottom: 12 }}>
                📅 {new Date(day.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}
                <span style={{ fontSize: 14, color: 'var(--text-light)', marginLeft: 8 }}>{day.day_of_week}</span>
              </h3>
              <div style={{ display: 'grid', gap: 6 }}>
                {Object.entries(day.dishes)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, count]) => (
                    <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ flex: 1, marginRight: 12 }}>{name}</span>
                      <span style={{ fontWeight: 600, color: 'var(--primary)', minWidth: 50, textAlign: 'right' }}>{count} шт.</span>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SummaryTab;
